import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Users,
  MessageSquareText,
  CalendarClock,
  ScrollText,
  Settings,
  LogOut,
  Workflow,
  Activity,
  PenTool,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { API } from "@/lib/api";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/templates", label: "Templates", icon: MessageSquareText, testid: "nav-templates", adminOnly: true },
  { to: "/schedule", label: "Schedule", icon: CalendarClock, testid: "nav-schedule", adminOnly: true },
  { to: "/logs", label: "Message Logs", icon: ScrollText, testid: "nav-logs" },
  { to: "/sync-logs", label: "Sync Logs", icon: Activity, testid: "nav-sync-logs", adminOnly: true },
  { to: "/diagnostics", label: "Diagnostics", icon: PenTool, testid: "nav-diagnostics", adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings", adminOnly: true },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const userRole = user?.role || "staff";
  const visibleItems = navItems.filter((item) => !item.adminOnly || userRole === "admin");
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetch(`${API}/branding/logo/exists`)
      .then((r) => r.json())
      .then((d) => { if (d.exists) setLogoUrl(`${API}/branding/logo?t=${Date.now()}`); })
      .catch(() => {});
  }, []);

  return (
    <aside className="w-64 border-r border-[#E2E8F0] bg-white flex flex-col h-screen sticky top-0">
      <div className="px-6 py-6 border-b border-[#E2E8F0]">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="w-9 h-9 rounded-md object-contain" />
          ) : (
            <div className="w-9 h-9 rounded-md bg-[#0A2540] flex items-center justify-center">
              <Workflow className="w-5 h-5 text-white" strokeWidth={1.8} />
            </div>
          )}
          <div>
            <div className="text-sm font-semibold tracking-tight text-[#0F172A]">FRESHNEL Middleware Accurate x Pancake</div>
            <div className="text-xs text-[#64748B]">Middleware</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[#0A2540] text-white"
                  : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
              }`
            }
          >
            <item.icon className="w-4 h-4" strokeWidth={1.8} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-[#E2E8F0] px-3 py-4">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-[#64748B]">Signed in as</div>
          <div className="text-sm font-medium text-[#0F172A] truncate" data-testid="current-user-email">
            {user?.email}
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={async () => {
            await logout();
            navigate("/login");
          }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-[#475569] hover:bg-[#FEE2E2] hover:text-[#DC2626] transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.8} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}
